import {
  UNCATEGORIZED_CATEGORY_ID,
  UNCATEGORIZED_CATEGORY_LABEL,
} from '@/constants/categoryFilters';
import type { CategoryFilterCategory } from '@/types/categoryFilters.types';
import type { DropdownOption } from '@/components/common/ui/Dropdown';

function realCategories(
  categories: CategoryFilterCategory[],
  selectedCategoryIds: string[]
): CategoryFilterCategory[] {
  const selected = new Set(selectedCategoryIds);
  return categories.filter(c => selected.has(c.id));
}

export function buildCategoryDropdownOptions(
  categories: CategoryFilterCategory[]
): DropdownOption<string>[] {
  return [
    { value: UNCATEGORIZED_CATEGORY_ID, label: UNCATEGORIZED_CATEGORY_LABEL },
    ...categories.map(c => ({ value: c.id, label: c.name })),
  ];
}

export function buildSegmentDropdownOptions(
  categories: CategoryFilterCategory[],
  selectedCategoryIds: string[]
): DropdownOption<string>[] {
  const chosen = realCategories(categories, selectedCategoryIds);
  if (chosen.length === 0) return [];

  const prefixed = chosen.length > 1;
  return chosen.flatMap(c =>
    c.segments.map(s => ({ value: s.id, label: prefixed ? `${c.name} · ${s.name}` : s.name }))
  );
}

export function isSegmentFilterEnabled(selectedCategoryIds: string[]): boolean {
  return selectedCategoryIds.some(id => id !== UNCATEGORIZED_CATEGORY_ID);
}

export function pruneSegmentIds(
  categories: CategoryFilterCategory[],
  selectedCategoryIds: string[],
  selectedSegmentIds: string[]
): string[] {
  if (!isSegmentFilterEnabled(selectedCategoryIds)) return [];
  const allowed = new Set(
    realCategories(categories, selectedCategoryIds).flatMap(c => c.segments.map(s => s.id))
  );
  return selectedSegmentIds.filter(id => allowed.has(id));
}

/** Parse persisted tab-filter values (legacy number or string). */
export function sanitizeOptionalTaxonomyId(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

export function sanitizeTaxonomyIdList(value: unknown): string[] {
  const items: unknown[] = Array.isArray(value) ? value : [value];
  return items.map(sanitizeOptionalTaxonomyId).filter((id): id is string => id !== undefined);
}
